import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { DeliveriesService } from '../../services/deliveries/deliveries.service';
import { DeliveriesActions } from './deliveries.actions';
import { ISipping, IGetShippingPaginatedResponse, Pagination } from '../../interfaces/deliveries.interface';
import { FileUploadService } from '../../services/file-upload/file-upload.service';

export interface DeliveriesStateModel {
  deliveries: Pagination<ISipping>;
  loading: boolean;
  delivery: ISipping | null;
  uploading: boolean;
  uploadError: string | null;
  highlightedId: number | null;
}

@State<DeliveriesStateModel>({
  name: 'deliveries',
  defaults: {
    deliveries: { items: [], page: 1, pageSize: 10, total: 0 },
    loading: false,
    delivery: null,
    uploading: false,
    uploadError: null,
    highlightedId: null,
  },
})

@Injectable()
export class DeliveriesState {
  constructor(
    private deliveriesService: DeliveriesService,
    private fileUploadService: FileUploadService
  ) {}

  @Selector()
  static getDeliveries(state: DeliveriesStateModel): Pagination<ISipping> {
    return state.deliveries;
  }

  @Selector()
  static isLoading(state: DeliveriesStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getDelivery(state: DeliveriesStateModel): ISipping | null {
    return state.delivery;
  }

  @Selector()
  static isUploading(state: DeliveriesStateModel): boolean {
    return state.uploading;
  }

  @Selector()
  static getUploadError(state: DeliveriesStateModel): string | null {
    return state.uploadError;
  }

  @Selector()
  static getHighlightedId(state: DeliveriesStateModel): number | null {
    return state.highlightedId;
  }

  @Action(DeliveriesActions.GetDeliveries)
  getDeliveries(ctx: StateContext<DeliveriesStateModel>, action: DeliveriesActions.GetDeliveries) {
    ctx.patchState({ loading: true });

    return this.deliveriesService.getDeliveries(action.page, action.pageSize, action.status, action.priority, action.q, action.driverId).pipe(
      tap({
        next: (response: IGetShippingPaginatedResponse) => {
          ctx.patchState({
            deliveries: response.data,
            loading: false,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(DeliveriesActions.SetDeliveries)
  setDeliveries(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.SetDeliveries
  ) {
    ctx.patchState({
      deliveries: action.deliveries,
    });
  }

  @Action(DeliveriesActions.GetDeliveryById)
  getDeliveryById(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.GetDeliveryById
  ) {
    return this.deliveriesService.getDeliveryById(action.id).pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            delivery: response.data,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(DeliveriesActions.UploadReceiptFile)
  uploadReceiptFile(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.UploadReceiptFile
  ) {
    ctx.patchState({
      uploading: true,
      uploadError: null
    });

    return this.fileUploadService.uploadShippingFile(
      action.shippingId,
      action.receiptId,
      action.file
    ).pipe(
      tap({
        next: (response) => {
          if (response.type === HttpEventType.Response) {
            ctx.dispatch(new DeliveriesActions.UploadReceiptFileSuccess(response));
            ctx.dispatch(new DeliveriesActions.GetDeliveryById(action.shippingId.toString()));
          }
        },
        error: (error) => {
          ctx.dispatch(new DeliveriesActions.UploadReceiptFileError(error.message));
        },
      }),
      catchError((error) => {
        ctx.dispatch(new DeliveriesActions.UploadReceiptFileError(error.message));
        return throwError(() => error);
      })
    );
  }

  @Action(DeliveriesActions.UploadReceiptFileSuccess)
  uploadReceiptFileSuccess(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.UploadReceiptFileSuccess
  ) {
    ctx.patchState({
      uploading: false,
      uploadError: null
    });
  }

  @Action(DeliveriesActions.UploadReceiptFileError)
  uploadReceiptFileError(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.UploadReceiptFileError
  ) {
    ctx.patchState({
      uploading: false,
      uploadError: action.error
    });
  }

  @Action(DeliveriesActions.HighlightRow)
  highlightRow(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.HighlightRow
  ) {
    ctx.patchState({ highlightedId: action.id });
  }

  @Action(DeliveriesActions.PatchDelivery)
  patchDelivery(
    ctx: StateContext<DeliveriesStateModel>,
    action: DeliveriesActions.PatchDelivery
  ) {
    const state = ctx.getState();
    const items = state.deliveries.items.map((item) =>
      item.id === action.delivery.id ? { ...item, ...action.delivery } : item
    );
    ctx.patchState({
      deliveries: { ...state.deliveries, items },
    });
  }
}
